import map from 'lodash/map'
import get from 'lodash/get'
import isUndefined from 'lodash/isUndefined'
import find from 'lodash/find'
import findLast from 'lodash/findLast'
import filter from 'lodash/filter'
import { pluralize } from 'inflected'

export const TITLE = 'title'
export const APPLICATIONS = 'applications'
export const SIDEBAR = 'sidebar'
export const BREADCRUMB = 'breadcrumb'
export const ACTIONS = 'actions'
export const ALFRED = 'alfred'

let _Vue
let _views
let _data

/**
 * Gets the view component from the passed Vue instance.
 */
function getView (vm) {
  return new Promise(resolve => {
    let component = vm.$options._parentVnode

    if (component && component.data.routerView) {
      resolve(component.componentInstance)
    }
  })
}

/**
 * Adds a view component to the view register.
 */
async function registerView (vm) {
  const component = await getView(vm)

  if (!viewIsRegistered(component)) {
    component._vid = _views.length
    _views.push(component)
  } else {
    return
  }

  [
    APPLICATIONS,
    SIDEBAR,
    TITLE,
    BREADCRUMB,
    ACTIONS
  ].map((property) => {
    // Whenever the view component updates the navigation properties ensure that
    // we update the navigation data.
    component.$watch(property, function () {
      updateNavigation()
      vm.$alfred.setActions(getAlfredActions(vm))
    }, { deep: true, immediate: true })
  })

  component.$once('hook:beforeDestroy', function () {
    // When the user navigates away from the current view, ensure that we
    // are no longer tracking the view.
    deregisterView(this)
  })
}

async function getAlfredActions (vm) {
  const component = await getView(vm)
  return get(component, ALFRED, [])
}

/**
 * Removes an existing view component from the view register.
 */
async function deregisterView (component) {
  if (viewIsRegistered(component)) {
    delete _views[component._vid]
  }
}

/**
 * Determines if the given view component has been registered.
 */
function viewIsRegistered (component) {
  return _views.hasOwnProperty(component._vid)
}

/**
 * Updates the navigation.
 */
async function updateNavigation () {
  _Vue.set(_data, TITLE, getTitle())
  _Vue.set(_data, APPLICATIONS, getApplications())
  _Vue.set(_data, SIDEBAR, getSidebar())
  _Vue.set(_data, pluralize(BREADCRUMB), getBreadcrumbs())
  _Vue.set(_data, ACTIONS, getActions())
}

/**
 * Gets the page title.
 */
function getTitle () {
  return get(findLast(_views, function (view) {
    return (view && !isUndefined(view[TITLE]))
  }), TITLE)
}

/**
 * Gets the applications.
 */
function getApplications () {
  return get(find(_views, function (view) {
    return (view && !isUndefined(view[APPLICATIONS]))
  }), APPLICATIONS, [])
}

/**
 * Gets the sidebar.
 */
function getSidebar () {
  return get(findLast(_views, function (view) {
    return (view && !isUndefined(view[SIDEBAR]))
  }), SIDEBAR, [])
}

/**
 * Gets the breadcrumbs.
 */
function getBreadcrumbs () {
  return map(filter(_views, function (view) {
    return (view && !isUndefined(view[BREADCRUMB]))
  }), BREADCRUMB, []).map((breadcrumb) => {
    return new Promise(function (resolve) {
      const name = Promise.resolve(breadcrumb.name)
      const route = Promise.resolve(breadcrumb.route)

      Promise.all([ name, route ]).then((breadcrumb) => {
        resolve({ name: breadcrumb[0], route: breadcrumb[1] })
      })
    })
  })
}

/**
 * Gets the actions.
 */
function getActions () {
  return get(findLast(_views, function (view) {
    return (view && !isUndefined(view[ACTIONS]))
  }), ACTIONS, [])
}

export default {
  install (Vue) {
    if (this.installed && _Vue === Vue) {
      return
    }

    this.installed = true

    _Vue = Vue
    _views = []
    _data = Vue.observable({})

    Object.defineProperty(Vue.prototype, '$navigation', {
      value: _data
    })

    Vue.mixin({
      created: function () {
        document
          .title = [
            ...new Set([ getTitle(), process.env.VUE_APP_TITLE ])
          ]
            .filter(Boolean)
            .join(' - ')
      },
      beforeMount: function () {
        registerView(this)
      }
    })

    updateNavigation()
  }
}
